import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, MapPin, LogOut, RefreshCw, Phone, Clock, Trash2, Users, Download, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/pages/LandingPage";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-primary/10 text-primary",
  contacted: "bg-amber-100 text-amber-700",
  closed: "bg-emerald-100 text-emerald-700",
};

export default function AdminDashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tab, setTab] = useState("inquiries");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "SolveDesk Admin — Leads";
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => robots.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1");
  }, []);

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("soldesk_admin_token")}` },
    withCredentials: true,
  }), []);

  const load = useCallback(async () => {
    try {
      const [inq, st, wl, cb] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, authHeaders()),
        axios.get(`${API}/admin/stats`, authHeaders()),
        axios.get(`${API}/admin/waitlist`, authHeaders()),
        axios.get(`${API}/admin/callbacks`, authHeaders()),
      ]);
      setInquiries(inq.data);
      setStats(st.data);
      setWaitlist(wl.data);
      setCallbacks(cb.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("soldesk_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not load inquiries");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, navigate]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, authHeaders());
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, authHeaders());
      setInquiries((list) => list.filter((i) => i.id !== id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Could not delete inquiry");
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, authHeaders()); } catch { /* ignore */ }
    localStorage.removeItem("soldesk_admin_token");
    navigate("/admin");
  };

  const filtered = statusFilter === "all" ? inquiries : inquiries.filter((i) => i.status === statusFilter);

  const safe = (v) => (v == null ? "" : String(v));
  const slugify = (v) => safe(v).toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const downloadCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/export/${kind}.csv`, { ...authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `soldesk_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind === "inquiries" ? "Inquiries" : "Waitlist"} CSV downloaded`);
    } catch {
      toast.error("Could not download CSV");
    }
  };

  const notifyLaunch = async () => {
    if (!window.confirm("Send the launch announcement email to all waitlist members with an email address?")) return;
    try {
      const { data } = await axios.post(`${API}/admin/waitlist/notify-launch`, {}, authHeaders());
      toast.success(`Launch announcement sent to ${data.sent} member${data.sent !== 1 ? "s" : ""}${data.without_email ? ` (${data.without_email} have no email on file)` : ""}`);
    } catch {
      toast.error("Could not send announcement");
    }
  };

  const removeWaitlist = async (id) => {
    if (!window.confirm("Remove this waitlist signup?")) return;
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, authHeaders());
      setWaitlist((list) => list.filter((i) => i.id !== id));
      toast.success("Signup removed");
    } catch {
      toast.error("Could not remove signup");
    }
  };

  const markCallbackDone = async (id) => {
    try {
      await axios.patch(`${API}/admin/callbacks/${id}`, { status: "done" }, authHeaders());
      setCallbacks((list) => list.map((c) => (c.id === id ? { ...c, status: "done" } : c)));
      toast.success("Marked as called");
    } catch {
      toast.error("Could not update");
    }
  };

  const removeCallback = async (id) => {
    if (!window.confirm("Delete this callback request?")) return;
    try {
      await axios.delete(`${API}/admin/callbacks/${id}`, authHeaders());
      setCallbacks((list) => list.filter((c) => c.id !== id));
      toast.success("Callback deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setLoading(true); load(); }} data-testid="refresh-inquiries-button" className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button" className="rounded-full text-slate-600">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-semibold text-slate-900" data-testid="dashboard-headline">Leads</h1>
        <p className="text-sm text-muted-foreground mt-1">Every inquiry and waitlist signup from the landing page lands here.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="dashboard-tabs">
          {[["inquiries", "Inquiries"], ["callbacks", "Callbacks"], ["waitlist", "Workspace Waitlist"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`rounded-full px-5 h-10 text-sm font-semibold transition-colors duration-200 ${
                tab === key ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"
              }`}>
              {label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadCsv("inquiries")} data-testid="export-inquiries-button" className="rounded-full">
              <Download className="w-4 h-4 mr-2" /> Inquiries CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv("waitlist")} data-testid="export-waitlist-button" className="rounded-full">
              <Download className="w-4 h-4 mr-2" /> Waitlist CSV
            </Button>
            <Button size="sm" onClick={notifyLaunch} data-testid="notify-launch-button"
              className="rounded-full bg-primary hover:bg-primary/90 text-white">
              <Megaphone className="w-4 h-4 mr-2" /> Notify Waitlist
            </Button>
          </div>
        </div>

        {stats && tab === "inquiries" && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-row">
            <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_8px_rgb(0,0,0,0.04)]" data-testid="stat-total">
              <Inbox className="w-5 h-5 text-primary mb-3" />
              <p className="font-display text-3xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs font-medium text-muted-foreground mt-1">Total inquiries</p>
            </div>
            <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_8px_rgb(0,0,0,0.04)]" data-testid="stat-new">
              <Clock className="w-5 h-5 text-primary mb-3" />
              <p className="font-display text-3xl font-bold text-slate-900">{stats.new}</p>
              <p className="text-xs font-medium text-muted-foreground mt-1">New / uncontacted</p>
            </div>
            {stats.by_city.slice(0, 1).map((c) => (
              <div key={c.city} className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_8px_rgb(0,0,0,0.04)]" data-testid={`stat-city-${slugify(c.city)}`}>
                <MapPin className="w-5 h-5 text-primary mb-3" />
                <p className="font-display text-3xl font-bold text-slate-900">{c.count}</p>
                <p className="text-xs font-medium text-muted-foreground mt-1">from {c.city}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_2px_8px_rgb(0,0,0,0.04)]" data-testid="stat-waitlist">
              <Users className="w-5 h-5 text-primary mb-3" />
              <p className="font-display text-3xl font-bold text-slate-900">{stats.waitlist}</p>
              <p className="text-xs font-medium text-muted-foreground mt-1">Workspace waitlist</p>
            </div>
          </div>
        )}

        {tab === "inquiries" && stats?.by_source?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-3" data-testid="source-chips">
            {stats.by_source.map((s) => (
              <span key={s.source} className="rounded-full bg-white border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-700" data-testid={`source-chip-${slugify(s.source)}`}>
                {s.source}: {s.count}
              </span>
            ))}
          </div>
        )}

        {tab === "callbacks" && (
          <div className="mt-8 space-y-4" data-testid="callbacks-list">
            {loading ? (
              <p className="text-sm text-muted-foreground py-12 text-center">Loading callbacks...</p>
            ) : callbacks.length === 0 ? (
              <div className="rounded-2xl bg-white border border-dashed border-slate-200 py-16 text-center" data-testid="callbacks-empty">
                <Phone className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No callback requests yet.</p>
              </div>
            ) : (
              callbacks.map((cb) => (
                <div key={cb.id} className="rounded-2xl bg-white border border-black/5 p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] flex flex-wrap items-center justify-between gap-4" data-testid={`callback-card-${cb.id}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg font-semibold text-slate-900">{cb.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${cb.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-primary/10 text-primary"}`} data-testid={`callback-status-${cb.id}`}>
                        {cb.status === "done" ? "Called" : "New"}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-primary" />{cb.phone}</span>
                      {cb.preferred_time && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" />{cb.preferred_time}</span>}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {new Date(cb.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {cb.status !== "done" && (
                      <Button size="sm" variant="outline" onClick={() => markCallbackDone(cb.id)}
                        data-testid={`callback-done-${cb.id}`} className="rounded-full">
                        Mark Called
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeCallback(cb.id)}
                      data-testid={`callback-delete-${cb.id}`} className="rounded-full text-destructive hover:bg-destructive/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "waitlist" && (
          <div className="mt-8 space-y-4" data-testid="waitlist-list">
            {loading ? (
              <p className="text-sm text-muted-foreground py-12 text-center">Loading waitlist...</p>
            ) : waitlist.length === 0 ? (
              <div className="rounded-2xl bg-white border border-dashed border-slate-200 py-16 text-center" data-testid="waitlist-empty">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No waitlist signups yet.</p>
              </div>
            ) : (
              waitlist.map((entry) => (
                <div key={entry.id} className="rounded-2xl bg-white border border-black/5 p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] flex flex-wrap items-center justify-between gap-4" data-testid={`waitlist-card-${entry.id}`}>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-slate-900">{entry.name}</h3>
                    <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-primary" />{entry.phone}</span>
                      {entry.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" />{entry.city}</span>}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {new Date(entry.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeWaitlist(entry.id)}
                    data-testid={`waitlist-delete-${entry.id}`} className="rounded-full text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "inquiries" && (
        <>
        <div className="mt-8 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700" data-testid="inquiry-count">{filtered.length} {filtered.length === 1 ? "inquiry" : "inquiries"}</p>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select" className="w-40 h-10 rounded-xl bg-white">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 space-y-4" data-testid="inquiries-list">
          {loading ? (
            <p className="text-sm text-muted-foreground py-12 text-center">Loading inquiries...</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-white border border-dashed border-slate-200 py-16 text-center" data-testid="inquiries-empty">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No inquiries yet. Share the landing page link in your ads.</p>
            </div>
          ) : (
            filtered.map((inq) => (
              <div key={inq.id} className="rounded-2xl bg-white border border-black/5 p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)]" data-testid={`inquiry-card-${inq.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg font-semibold text-slate-900">{inq.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide ${STATUS_STYLES[inq.status] || STATUS_STYLES.new}`} data-testid={`inquiry-status-${inq.id}`}>
                        {inq.status}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
                      <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-primary" />{inq.phone}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" />{inq.city}</span>
                      <span>{inq.business_type}</span>
                      <span className="font-medium text-slate-900">{inq.duration} months</span>
                    </div>
                    {inq.message && <p className="mt-2 text-sm text-muted-foreground">&ldquo;{inq.message}&rdquo;</p>}
                    {inq.utm_source && (
                      <p className="mt-2 text-xs font-medium text-primary" data-testid={`inquiry-utm-${inq.id}`}>
                        Ad source: {inq.utm_source}{inq.utm_medium ? ` / ${inq.utm_medium}` : ""}{inq.utm_campaign ? ` / ${inq.utm_campaign}` : ""}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      {new Date(inq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {inq.status === "new" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(inq.id, "contacted")}
                        data-testid={`inquiry-contacted-${inq.id}`} className="rounded-full">
                        Mark Contacted
                      </Button>
                    )}
                    {inq.status !== "closed" && (
                      <Button size="sm" variant="outline" onClick={() => updateStatus(inq.id, "closed")}
                        data-testid={`inquiry-closed-${inq.id}`} className="rounded-full">
                        Close
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(inq.id)}
                      data-testid={`inquiry-delete-${inq.id}`} className="rounded-full text-destructive hover:bg-destructive/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </>
        )}
      </main>
    </div>
  );
}
